<template>
<div class="shadow-sm">
    <b-navbar id="nev-bar" toggleable="lg" type="light" fixed="top" class="nev-bar-bg shadow-sm" :dir="isHebrew ? 'rtl' : 'ltr'">
        <b-container>
            <b-navbar-toggle
              v-if="!isSubdomain"
              target="nav-collapse"
            ></b-navbar-toggle>
            <b-navbar-brand
                @click="goToLink(logoNavPath)"
                id="header-logo">
                <img 
                    :src="getTheme.logo"
                    height="37px"
                    width="156px"
                    class="clikable"
                    alt="tripplanner">
            </b-navbar-brand>

            <b-collapse v-if="!isSubdomain" id="nav-collapse" class='main-menu' is-nav>
                <b-navbar-nav >
                    <b-nav-item style="margin-left:1.5rem; margin-right:1.5rem;" class='main-menu' @click="goToLink(internalHomePath)">{{ t('home') }}</b-nav-item>
                    <b-nav-item style="margin-left:1.5rem; margin-right:1.5rem;" @click="goToLink('/about')">{{ t('about') }}</b-nav-item>
                    <b-nav-item style="margin-left:1.5rem; margin-right:1.5rem;" v-if="isUserManager" @click="goToLink('/tutorials')">{{ t('tutorials') }}</b-nav-item>
                    <b-nav-item style="margin-left:1.5rem; margin-right:1.5rem;" v-if="isAppLoggedIn && isUserManager" @click="goToLink('/pricing')">{{ t('sharingUsageCostsNav') }}</b-nav-item>
                    <b-nav-item style="margin-left:1.5rem; margin-right:1.5rem;" v-if="isAppLoggedIn" @click="goToLink('/user_rides')">{{ t('myTrips') }}</b-nav-item>
                    <b-nav-item style="margin-left:1.5rem; margin-right:1.5rem; font-weight:600;" v-if="isAdmin" @click="goToLink('/admin')">Admin</b-nav-item>
                </b-navbar-nav>
            </b-collapse>

            <a
             v-if="!isSubdomain"
             href='https://www.facebook.com/tripplanner.co.il'
             aria-label="goo to TripPlanner facebook page"
             target='_blank'>
                <img
                src="https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/icons%2FFacebook_Logo_Primary.png?alt=media&token=2731adce-75a0-493d-ac5d-94a2913b2aa4"
                height="24px"
                width="26px"
                alt="facebookIcon"
                class="nev-bar_facebook_icon"
                @error="handleImageError($event, 'NavBar')">
            </a>

            <div v-if="isAppLoggedIn" class="horizontal logged-in-section">
              <b-dropdown
                ref="userMenu"
                right
                boundary="viewport"
                no-caret
                variant="link"
                toggle-class="user-menu-toggle p-0 border-0 shadow-none"
                menu-class="user-menu-dropdown"
                @shown="onUserMenuShown"
              >
                <template #button-content>
                  <div class="user-avatar-circle" :title="displayUserIdentity">
                    {{ userInitial }}
                  </div>
                </template>
                <div class="user-menu-content">
                  <div class="user-menu-label">{{ t('loggedInAs') }}</div>
                  <div class="user-menu-email">{{ displayUserIdentity }}</div>
                  <div v-if="isUserManager" class="user-role-badge">{{ t('rideManagerRole') }}</div>
                </div>
                <b-dropdown-divider></b-dropdown-divider>
                <template v-if="isUserManager">
                  <b-dropdown-item-button @click="goToLink('/my-referral-link')">
                    {{ t('myReferralLink') }}
                  </b-dropdown-item-button>
                  <!-- Account level, not ride level: linking a manager's own
                       WhatsApp binds HIS phone to HIS account and applies to
                       every ride he is billed for, so it is reached from the
                       account menu and opens a global popup rather than
                       navigating anywhere. -->
                  <b-dropdown-item-button @click="openMyWhatsappNumber">
                    {{ t('myWhatsappNumberMenuItem') }}
                  </b-dropdown-item-button>
                  <b-dropdown-divider></b-dropdown-divider>
                </template>
                <b-dropdown-item-button class="user-menu-logout" @click="appLogOut">
                  {{ t('logout') }}
                </b-dropdown-item-button>
              </b-dropdown>
            </div>
            <div v-else >
                <a class="trip-header-btn" @click="openLoginForm">{{ t('login') }}</a>
            </div>
        </b-container>
    </b-navbar>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';
import imageErrorMixin from '@/mixins/imageErrorMixin';
import { INTERNAL_HOME_PATH, resolveLogoPath } from '@/common/appHome';

export default {
  name: 'AppHeader',
  mixins: [translationMixin, imageErrorMixin],
  computed: { ...mapGetters([ 'isAppLoggedIn',
                              'userName',
                              'email',
                              'isUserManager',
                              'userId',
                            ]),
              isAdmin() {
                return this.userId === 1;
              },
              ...mapGetters("theme",  [ "getTheme",
                                        "isSubdomain",
                                      ]),
              displayUserIdentity() {
                return this.userName || this.email || this.t('loggedInUserFallback');
              },
              userInitial() {
                const identity = (this.userName || this.email || '').trim();
                if (identity) {
                  return identity.charAt(0).toUpperCase();
                }
                return 'U';
              },
              internalHomePath() {
                return INTERNAL_HOME_PATH;
              },
              logoNavPath() {
                const path = resolveLogoPath(this.getTheme.logoURL);
                if (this.isEnglish && path === '/') {
                  return '/welcome-ride-managers';
                }
                return path;
              },
  },

  methods: { ...mapActions(['login',
                            'appLogOut',
                            'validateAndupdateAppToken',
                             'openLoginForm',
                             'showModal']),
             ...mapActions('language', ['loadLanguageFromStorage']),
      onUserMenuShown() {
      this.$nextTick(() => {
        const dropdownRoot = this.$refs.userMenu && this.$refs.userMenu.$el;
        if (!dropdownRoot) return;
        const toggleEl = dropdownRoot.querySelector('button');
        const menuEl = dropdownRoot.querySelector('.dropdown-menu');
        if (!toggleEl || !menuEl) return;
        const viewportPadding = 8;
        let menuRect = menuEl.getBoundingClientRect();
        const menuStyle = window.getComputedStyle(menuEl);
        if (menuRect.left < viewportPadding || menuRect.right > window.innerWidth - viewportPadding) {
          const currentLeft = parseFloat(menuStyle.left) || 0;
          const overflowLeft = viewportPadding - menuRect.left;
          const overflowRight = menuRect.right - (window.innerWidth - viewportPadding);
          const shift = overflowLeft > 0 ? overflowLeft : (overflowRight > 0 ? -overflowRight : 0);
          menuEl.style.left = `${currentLeft + shift}px`;
          menuEl.style.right = 'auto';
          menuRect = menuEl.getBoundingClientRect();
        }
        const logoutButton = dropdownRoot.querySelector('.user-menu-logout .dropdown-item');
        if (!logoutButton) return;
        const docDir = document.documentElement.getAttribute('dir') || '';
        if (docDir === 'rtl') {
          logoutButton.style.textAlign = 'right';
          logoutButton.style.direction = 'rtl';
        } else {
          logoutButton.style.textAlign = 'left';
          logoutButton.style.direction = 'ltr';
        }
      });
    },
      openMyWhatsappNumber() {
      // The same channel every other global modal uses: showModal closes
      // whatever else is open and raises this one from the Popups registry.
      this.showModal('myWhatsappNumber');
    },
      goToLink( link ) {
      if( link )
        this.$router.push(link)
    },
  },
  created() {
    this.validateAndupdateAppToken();
    this.loadLanguageFromStorage();
  }

};
</script>

<style scoped>

.form-popup {
  display: none;
}

::v-deep .nav-link {
  font-style: normal;
  font-size: 21px;
  line-height: 24px;
  font-weight: 100;
  color: #1e1e1e;
  padding: 8px 5px !important;
  text-align: start;
}

::v-deep .navbar-light .navbar-nav .nav-link {
  color: #1e1e1e;
  transition: all 0.4s;
}

::v-deep .container {
  margin: 0 auto;
}

@media (min-width: 1200px) {
  ::v-deep .container {
    max-width: 1200px;
  }
}

@media (min-width: 1500px) {
  ::v-deep .container {
    max-width: 1360px;
  }
}

.shadow-sm {
  box-shadow: 0 .8rem .5rem rgba(0, 0, 0, .075) !important;
}


* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

/* Set font for body */
body {
  font-family: 'Noto Sans Hebrew', sans-serif;
  color: #555;
  text-align: right;
}

/* Navbar background and styling */
.nev-bar-bg {
  background-color: white;
  min-height: 50px;
}

/* Navigation item styles */
.main-menu {
  font-weight: 700;
  font-size: 18px;
  color: white;
  font-family: 'Noto Sans Hebrew', sans-serif;
}

/* Trip header button styling */
a.trip-header-btn {
  display: inline-block;
  font-size: 21px;
  font-weight: 400;
  color: #1f1f1f;
  padding: 8px 21px;
  border-radius: 50px;
  margin-right: 45px;
  font-family: 'Noto Sans Hebrew', sans-serif;
}

a.trip-header-btn:hover {
  cursor: pointer;
}

.logged-in-section {
  align-items: center;
  margin-right: 1.75rem;
}

html[dir='ltr'] .logged-in-section {
  margin-right: 0;
  margin-left: 1.75rem;
}

.user-avatar-circle {
  width: 38px;
  height: 38px;
  margin-top: 6px;
  border-radius: 50%;
  background-color: #1a73e8;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Noto Sans Hebrew', sans-serif;
}

.user-avatar-circle:hover {
  filter: brightness(0.95);
}

.user-menu-toggle {
  display: flex;
  align-items: center;
  line-height: 0;
  text-decoration: none !important;
}

.user-menu-toggle:focus,
.user-menu-toggle:active {
  box-shadow: none !important;
  outline: none !important;
  text-decoration: none !important;
}

::v-deep(.user-menu-toggle.dropdown-toggle::after),
::v-deep(.dropdown-toggle-no-caret::after) {
  display: none !important;
  content: none !important;
  border: 0 !important;
  margin: 0 !important;
}

.user-menu-content {
  padding: 0.85rem 1rem 0.65rem;
  text-align: right;
}

html[dir='ltr'] .user-menu-content {
  text-align: left;
}

.user-menu-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.user-menu-email {
  font-size: 14px;
  color: #1f1f1f;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
}

html[dir='ltr'] .user-menu-email {
  text-align: left;
}

.user-role-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.45rem;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  color: #1d4ed8;
  background: #dbeafe;
}

.user-menu-dropdown {
  left: auto !important;
  right: 0 !important;
  min-width: 13.5rem;
  max-width: calc(100vw - 1.5rem);
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.16);
  padding: 0.25rem 0;
  overflow: hidden;
}

html[dir='rtl'] ::v-deep(.user-menu-dropdown .user-menu-logout .dropdown-item) {
  text-align: right !important;
  direction: rtl !important;
}

html[dir='ltr'] ::v-deep(.user-menu-dropdown .user-menu-logout .dropdown-item) {
  text-align: left !important;
  direction: ltr !important;
}

::v-deep(.user-menu-dropdown .dropdown-divider) {
  margin: 0.25rem 0;
  border-top-color: #e5e7eb;
}

::v-deep(.user-menu-dropdown .dropdown-item) {
  padding: 0.58rem 1rem;
  font-size: 16px;
  font-weight: 500;
  color: #111827;
}

::v-deep(.user-menu-dropdown .dropdown-item:hover) {
  background-color: #f3f4f6;
}

/* Facebook icon in navbar */
.nev-bar_facebook_icon {
  height: 38px;
  width: 38px;
  margin-top: 6px;
}


/* Import font */
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;600;700&family=Rubik:wght@400;500;700&display=swap');

/* Base Font and Styles */
body {
  font-family: 'Noto Sans Hebrew', sans-serif;
}

/* Navbar and Button Style Adjustments */

/* For Mobile (320px - 480px) */
@media (max-width: 30em) { /* 480px */
  .nev-bar-bg {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  a.trip-header-btn {
    padding: 2px 5px;
    margin-right: 0;
    font-size: 14px;
  }

  .user-menu-email {
    max-width: 140px;
  }

  .user-menu-dropdown {
    right: -80px !important;
    left: auto !important;
    min-width: 11.5rem;
    max-width: calc(100vw - 1rem);
    transform: none !important;
  }

  .user-avatar-circle {
    width: 34px;
    height: 34px;
    margin-top: 4px;
    font-size: 14px;
  }

  .nev-bar_facebook_icon {
    margin-top: 4px;
  }

  .logged-in-section {
    margin-right: 1.25rem;
  }

  html[dir='ltr'] .logged-in-section {
    margin-right: 0;
    margin-left: 1.25rem;
  }

  .nev-bar_facebook_icon {
    display: none;
  }
}

/* Tablet and Small Screen Sizes (481px - 768px) */
@media (max-width: 48em) { /* 768px */
  .trip-header-section {
    padding-top: 2rem;
    padding-bottom: 1rem;
  }
  .trip-header-btn {
    font-size: 16px;
  }

  .main-menu {
    font-size: 16px;
    margin-right: 1rem;
  }
  .global_header_text {
    margin-right: 5rem;
  }

  /* Trip Header Logo Adjustments */
  #header-logo img {
    width: 100px;
    height: auto;
  }
}

/* Medium Screens (769px - 1024px) */
@media (max-width: 64em) { /* 1024px */
  .trip-header-section {
    padding: 1rem 0;
  }

  .trip-header-btn {
    font-size: 18px;
    padding: 6px 15px;
    margin-right: 10px;
  }

  .main-menu {
    font-size: 18px;
  }
}

/* Large Screens (1025px - 1200px) */
@media (max-width: 75em) { /* 1200px */
  /* Minor adjustments for larger screens */
  .trip-header-btn {
    font-size: 20px;
    padding: 8px 20px;
  }

  .main-menu {
    font-size: 20px;
  }
}

/* For 360px Screens and Below */
@media (max-width: 22.5em) { /* 360px */
  .trip-card-item {
    padding: 10px;
  }
  .corner-image {
    top: -19px;
    right: -3px;
  }
}

</style>

